export interface RecordedEvent {
  ts: number;
  type: 'mutation' | 'scroll' | 'input' | 'click' | 'resize' | 'snapshot';
  data: Record<string, any>;
}

export interface SessionRecordingConfig {
  maxDurationMs?: number;
  maxEvents?: number;
  sampleScrollEvery?: number;
  maskInputs?: boolean;
  maskSelectors?: string[];
}

export interface SessionRecording {
  events: RecordedEvent[];
  startedAt: string;
  duration_ms: number;
  viewport: { width: number; height: number };
}

export interface SessionRecordingInstance {
  stop(): SessionRecording;
  isRecording(): boolean;
  getEventCount(): number;
  getDurationMs(): number;
}

const DEFAULT_CONFIG: Required<SessionRecordingConfig> = {
  maxDurationMs: 120_000,
  maxEvents: 5000,
  sampleScrollEvery: 200,
  maskInputs: true,
  maskSelectors: [],
};

const DEFAULT_MASK_SELECTORS = [
  'input[type="password"]',
  '[data-mask]',
];

export function startRecording(
  config?: SessionRecordingConfig
): SessionRecordingInstance {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const allMaskSelectors = [...DEFAULT_MASK_SELECTORS, ...cfg.maskSelectors];

  const events: RecordedEvent[] = [];
  let recording = true;
  const startTime = Date.now();
  const startedAt = new Date().toISOString();
  let lastScrollTs = 0;
  const cleanups: (() => void)[] = [];

  function addEvent(event: RecordedEvent): void {
    if (!recording) return;

    if (events.length >= cfg.maxEvents) {
      dropOldestNonSnapshot();
    }

    events.push(event);

    if (events.length >= cfg.maxEvents || Date.now() - startTime >= cfg.maxDurationMs) {
      stopRecording();
    }
  }

  function dropOldestNonSnapshot(): void {
    for (let i = 0; i < events.length; i++) {
      if (events[i].type !== 'snapshot') {
        events.splice(i, 1);
        return;
      }
    }
  }

  function relativeTs(): number {
    return Date.now() - startTime;
  }

  function shouldMaskElement(el: Element): boolean {
    if (!cfg.maskInputs) return false;
    if (el instanceof HTMLInputElement && el.type === 'password') return true;
    for (const sel of allMaskSelectors) {
      if (el.matches(sel)) return true;
    }
    return false;
  }

  function sanitizeHtml(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (['script', 'style', 'link', 'noscript'].includes(tag)) return '';

    let html = `<${tag}`;
    for (const attr of Array.from(el.attributes)) {
      if (attr.name === 'value' && shouldMaskElement(el)) {
        html += ` ${attr.name}="•••"`;
      } else {
        html += ` ${attr.name}="${attr.value.replace(/"/g, '&quot;')}"`;
      }
    }
    html += '>';

    if (['br', 'hr', 'img', 'input'].includes(tag)) return html;

    for (const child of Array.from(node.childNodes)) {
      html += sanitizeHtml(child);
    }
    html += `</${tag}>`;
    return html;
  }

  // Initial DOM snapshot
  const snapshotHtml = sanitizeHtml(document.documentElement);
  addEvent({
    ts: 0,
    type: 'snapshot',
    data: {
      html: snapshotHtml.substring(0, 500_000),
      doctype: document.doctype?.name ?? 'html',
    },
  });

  // MutationObserver for DOM changes
  const observer = new MutationObserver((mutations) => {
    const summary: Record<string, any>[] = [];
    for (const m of mutations) {
      if (m.type === 'childList') {
        summary.push({
          type: 'childList',
          target: cssPath(m.target),
          addedCount: m.addedNodes.length,
          removedCount: m.removedNodes.length,
        });
      } else if (m.type === 'attributes') {
        summary.push({
          type: 'attributes',
          target: cssPath(m.target),
          attr: m.attributeName,
        });
      } else if (m.type === 'characterData') {
        summary.push({
          type: 'characterData',
          target: cssPath(m.target),
        });
      }
    }
    if (summary.length > 0) {
      addEvent({ ts: relativeTs(), type: 'mutation', data: { mutations: summary } });
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
    attributeOldValue: false,
  });
  cleanups.push(() => observer.disconnect());

  // Click handler
  const onClick = (e: MouseEvent) => {
    const target = e.target as Element;
    addEvent({
      ts: relativeTs(),
      type: 'click',
      data: {
        x: e.clientX,
        y: e.clientY,
        target: cssPath(target),
        tag: target?.tagName?.toLowerCase(),
        text: (target?.textContent ?? '').substring(0, 50),
      },
    });
  };
  document.addEventListener('click', onClick, { capture: true });
  cleanups.push(() => document.removeEventListener('click', onClick, { capture: true } as any));

  // Scroll handler (throttled)
  const onScroll = () => {
    const now = Date.now();
    if (now - lastScrollTs < cfg.sampleScrollEvery) return;
    lastScrollTs = now;
    addEvent({
      ts: relativeTs(),
      type: 'scroll',
      data: {
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      },
    });
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  cleanups.push(() => document.removeEventListener('scroll', onScroll, { capture: true } as any));

  // Input handler
  const onInput = (e: Event) => {
    const target = e.target as Element;
    let value: string;
    if (shouldMaskElement(target)) {
      value = '•••';
    } else if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      value = cfg.maskInputs ? '•••' : target.value.substring(0, 100);
    } else {
      value = '•••';
    }
    addEvent({
      ts: relativeTs(),
      type: 'input',
      data: {
        target: cssPath(target),
        tag: target?.tagName?.toLowerCase(),
        value,
      },
    });
  };
  document.addEventListener('input', onInput, { capture: true });
  cleanups.push(() => document.removeEventListener('input', onInput, { capture: true } as any));

  // Resize handler
  const onResize = () => {
    addEvent({
      ts: relativeTs(),
      type: 'resize',
      data: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });
  };
  window.addEventListener('resize', onResize);
  cleanups.push(() => window.removeEventListener('resize', onResize));

  // Auto-stop timer
  const timer = setTimeout(() => {
    if (recording) stopRecording();
  }, cfg.maxDurationMs);
  cleanups.push(() => clearTimeout(timer));

  function stopRecording(): SessionRecording {
    if (!recording) {
      return buildResult();
    }
    recording = false;
    for (const cleanup of cleanups) {
      try { cleanup(); } catch { /* ignore */ }
    }
    return buildResult();
  }

  function buildResult(): SessionRecording {
    return {
      events: [...events],
      startedAt,
      duration_ms: Date.now() - startTime,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };
  }

  return {
    stop: () => stopRecording(),
    isRecording: () => recording,
    getEventCount: () => events.length,
    getDurationMs: () => Date.now() - startTime,
  };
}

function cssPath(node: Node): string {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return node.parentElement ? cssPath(node.parentElement) : '';
  }
  const el = node as Element;
  if (el.id) return `#${el.id}`;

  const tag = el.tagName.toLowerCase();
  if (tag === 'html' || tag === 'body') return tag;

  const parent = el.parentElement;
  if (!parent) return tag;

  const siblings = Array.from(parent.children).filter(
    (c) => c.tagName === el.tagName
  );
  if (siblings.length === 1) {
    return `${cssPath(parent)} > ${tag}`;
  }
  const index = siblings.indexOf(el) + 1;
  return `${cssPath(parent)} > ${tag}:nth-child(${index})`;
}
